import { assert, isFunction } from '../validation/index.mjs';
export const cloneHandlers = new Map();
export const mergeHandlers = new Map();
export const equalityHandlers = new Map();
/**
 * Properties that are dangerous for prototype pollution
 */ const DANGEROUS_PROPS = new Set([
    '__proto__',
    'constructor',
    'prototype'
]);
/**
 * Checks if a property key is dangerous for prototype pollution
 *
 * @param key - The property key to check
 * @returns true if the key is dangerous and should be skipped
 *
 * @example
 * isDangerousKey('__proto__'); // true
 * isDangerousKey('constructor'); // true
 * isDangerousKey('normalProp'); // false
 */ export const isDangerousKey = (key)=>{
    return typeof key === 'string' && DANGEROUS_PROPS.has(key);
};
/**
 * Filters out dangerous properties from an object's keys to prevent prototype pollution
 *
 * @param obj - The object to get safe keys from
 * @returns Array of safe property keys
 *
 * @example
 * const obj = { a: 1, __proto__: {}, constructor: Function };
 * getSafeKeys(obj); // ['a']
 */ export const getSafeKeys = (obj)=>{
    return Object.keys(obj).filter((key)=>!isDangerousKey(key));
};
/**
 * Filters out dangerous properties when iterating over object entries
 *
 * @param obj - The object to get safe entries from
 * @returns Array of safe [key, value] pairs
 *
 * @example
 * const obj = { a: 1, __proto__: {}, b: 2 };
 * getSafeEntries(obj); // [['a', 1], ['b', 2]]
 */ export const getSafeEntries = (obj)=>{
    return Object.entries(obj).filter(([key])=>!isDangerousKey(key));
};
/**
 * Add a handler for a specific function
 * @param fn - the function to add the handler for
 * @param cnstr - the constructor to add the handler for
 * @param handler - the handler to add

* @example
 * addHandlerFor('clone', FooClass, (original, seen) => new FooClass(original.bar, original.baz));
 * addHandlerFor('equals', FooClass, (a, b, seen) => equals(a.bar, b.bar, seen) && equals(a.baz, b.baz, seen));
 * addHandlerFor('merge', FooClass, (target, source, opts) => merge(target.bar, source.bar, opts));
 */ export const addHandlerFor = (fn, cnstr, handler)=>{
    assert(/(clone|merge|equals)/.test(fn), 'invalid function name');
    assert(isFunction(handler), 'handler is not a function');
    if (fn === 'clone') {
        cloneHandlers.set(cnstr, handler);
    }
    if (fn === 'equals') {
        equalityHandlers.set(cnstr, handler);
    }
    if (fn === 'merge') {
        mergeHandlers.set(cnstr, handler);
    }
};
